import { useRef, type ChangeEvent, type DragEvent, type ReactNode } from "react";
import { cn } from "../utils/cx.js";
import { Button } from "./Button.js";
import { Icon } from "./Icon.js";

export interface UploadProps {
  onChange?: (files: File[]) => void;
  multiple?: boolean;
  accept?: string;
  disabled?: boolean;
  maxCount?: number;
  dropzone?: boolean;
  children?: ReactNode;
  className?: string;
}

export function Upload({
  onChange,
  multiple,
  accept,
  disabled,
  maxCount,
  dropzone,
  children,
  className,
}: UploadProps) {
  const ref = useRef<HTMLInputElement>(null);

  const take = (list: FileList | null) => {
    if (!list) return;
    let files = Array.from(list);
    if (maxCount != null) files = files.slice(0, maxCount);
    onChange?.(files);
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    take(e.target.files);
    e.target.value = "";
  };

  const hiddenInput = (
    <input
      ref={ref}
      type="file"
      accept={accept}
      multiple={multiple}
      disabled={disabled}
      className="hidden"
      onChange={onInputChange}
    />
  );

  if (dropzone) {
    const onDrop = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!disabled) take(e.dataTransfer.files);
    };
    return (
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && ref.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className={cn(
          "flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed border-input px-6 py-8 text-center text-muted-foreground transition-colors hover:border-ring hover:bg-muted/40",
          disabled && "pointer-events-none opacity-50",
          className,
        )}
      >
        {hiddenInput}
        <Icon name="upload" size={24} />
        <p className="text-sm">{children ?? "Tıkla veya dosyaları buraya sürükle"}</p>
      </div>
    );
  }

  return (
    <span className={className}>
      {hiddenInput}
      <Button icon={<Icon name="upload" />} disabled={disabled} onClick={() => ref.current?.click()}>
        {children ?? "Dosya seç"}
      </Button>
    </span>
  );
}
